import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Buildings, CalendarBlank, Coins, DownloadSimple, EnvelopeSimple, FileXls, GraduationCap, PaperPlaneTilt, Plus, Trash, UploadSimple, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError, useAuth } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MedcofImportBlock from "../components/MedcofImportBlock";
import CorrectionMaintenanceBlock from "../components/CorrectionMaintenanceBlock";
import StudentStampsBlock from "../components/StudentStampsBlock";
import ExamStyleManagementBlock from "../components/ExamStyleManagementBlock";
import SchoolLayoutAccessBlock from "../components/SchoolLayoutAccessBlock";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

/* ---------- Section wrapper ---------- */
const Section = ({ eyebrow, title, right, children }) => (
  <div className="border border-border bg-card mb-6">
    <div className="border-b border-border px-6 py-4 flex items-center justify-between">
      <div>
        <div className="label-eyebrow">{eyebrow}</div>
        <div className="font-display text-2xl font-bold tracking-tight mt-1">{title}</div>
      </div>
      {right}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

/* ---------- Native spreadsheet picker / drop target ---------- */
function QuestionSpreadsheetPicker({
  inputId,
  inputRef,
  disabled = false,
  onFile,
  buttonTestId,
  dropzoneTestId,
  label,
}) {
  const [dragActive, setDragActive] = useState(false);

  const receiveFile = (file) => {
    if (!file || disabled) return;
    const name = String(file.name || "").toLowerCase();
    if (!name.endsWith(".xlsx")) {
      toast.error("Envie uma planilha XLSX (.xlsx)");
      return;
    }
    onFile(file);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    if (!disabled) setDragActive(true);
  };

  const onDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    receiveFile(event.dataTransfer?.files?.[0]);
  };

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={dropzoneTestId}
      className={`mt-5 border-2 border-dashed p-5 text-center transition-colors ${
        disabled
          ? "border-border bg-muted/20 opacity-70 cursor-not-allowed"
          : dragActive
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary bg-muted/10"
      }`}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="sr-only"
        data-testid={inputId}
        onChange={(event) => receiveFile(event.target.files?.[0])}
      />
      {disabled ? (
        <Button type="button" disabled data-testid={buttonTestId} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
          <UploadSimple size={14} weight="bold" className="mr-2" />{label}
        </Button>
      ) : (
        <Button asChild type="button" data-testid={buttonTestId} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
          <label htmlFor={inputId} className="cursor-pointer"><UploadSimple size={14} weight="bold" className="mr-2" />{label}</label>
        </Button>
      )}
      <div className="text-xs text-muted-foreground mt-3">ou arraste uma planilha XLSX para esta área</div>
    </div>
  );
}

/* ---------- Admin-only permission preview ---------- */
function ImpersonationBlock() {
  const { setUser } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2 || selected) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const response = await api.get(`/users/search?q=${encodeURIComponent(query.trim())}&include_inactive=true`);
        setResults((response.data || []).filter((item) => !item.is_admin));
      } catch (error) {
        setResults([]);
        toast.error(fmtError(error));
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, selected]);

  const start = async () => {
    if (!selected) return;
    if (!window.confirm(`Visualizar o sistema com as permissões de ${selected.name || selected.email}?`)) return;
    setStarting(true);
    try {
      const response = await api.post(`/admin/impersonation/start/${selected.id}`);
      setUser(response.data.user);
      window.location.href = "/dashboard";
    } catch (error) {
      toast.error(fmtError(error));
      setStarting(false);
    }
  };

  return (
    <Section eyebrow="segurança · teste de acesso" title="Visualizar como usuário">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end">
        <div className="relative">
          <div className="label-eyebrow mb-1">usuário existente</div>
          {selected ? (
            <div className="border border-primary p-3 flex items-center justify-between gap-3" data-testid="impersonation-selected-user">
              <div>
                <div className="font-mono-data font-bold">{selected.name}</div>
                <div className="text-xs text-muted-foreground">{selected.email}</div>
                <div className="text-[10px] uppercase tracking-widest text-primary mt-1">{selected.active ? "usuário ativo" : "aguardando ativação · disponível para teste"}</div>
              </div>
              <button className="text-xs text-primary" onClick={() => { setSelected(null); setQuery(""); }}>trocar</button>
            </div>
          ) : (
            <>
              <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Digite nome ou e-mail…" className="rounded-none" data-testid="impersonation-user-search" />
              {results.length > 0 && (
                <div className="absolute z-20 left-0 right-0 border border-border bg-card max-h-56 overflow-y-auto shadow-lg">
                  {results.map((item) => (
                    <button key={item.id} onClick={() => { setSelected(item); setResults([]); }} className="w-full text-left p-3 border-b border-border last:border-0 hover:bg-muted/40">
                      <span className="font-mono-data text-sm font-semibold">{item.name}</span>
                      <span className="block text-xs text-muted-foreground">{item.email}</span>
                      <span className="block text-[10px] uppercase tracking-widest text-primary mt-1">{item.active ? "ativo" : "aguardando ativação"}{item.student_id ? " · aluno" : ""}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <Button onClick={start} disabled={!selected || starting} className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="start-impersonation">
          <Users size={16} className="mr-2" /> {starting ? "Iniciando…" : "Testar permissões"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        Você navegará com as permissões efetivas desse usuário, inclusive se a conta ainda estiver aguardando ativação. Senha, MFA e gerenciamento de sessões ficam bloqueados. Uma faixa permanente permitirá retornar ao administrador.
      </p>
    </Section>
  );
}

/* ---------- Admin: temporary student-mobile test access ---------- */
function MobileStudentTestBlock() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState("");
  const [issued, setIssued] = useState(null);

  const loadStatus = async ({ quiet = false } = {}) => {
    if (!quiet) setLoadingStatus(true);
    try {
      const response = await api.get("/admin/student-mobile-test/status");
      setStatus(response.data || {});
    } catch (error) {
      if (!quiet) toast.error(`Não foi possível consultar o modo de teste: ${fmtError(error)}`);
      setStatus(null);
    } finally {
      if (!quiet) setLoadingStatus(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  useEffect(() => {
    if (query.trim().length < 2 || selected) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const response = await api.get(`/users/search?q=${encodeURIComponent(query.trim())}&include_inactive=true`);
        setResults((response.data || []).filter((item) => !item.is_admin && item.student_id && item.active));
      } catch (error) {
        setResults([]);
        toast.error(fmtError(error));
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, selected]);

  const requirePassword = () => {
    if (password.trim()) return true;
    toast.error("Confirme sua senha de administrador para continuar.");
    return false;
  };

  const enable = async () => {
    if (!requirePassword()) return;
    setBusy("enable");
    try {
      const response = await api.post("/admin/student-mobile-test/enable", { password });
      setStatus(response.data || { enabled: true });
      setPassword("");
      setIssued(null);
      toast.success("Modo temporário de teste ativado.");
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setBusy("");
    }
  };

  const disable = async () => {
    if (!requirePassword()) return;
    if (!window.confirm("Desligar o modo de teste? Todos os acessos temporários em celulares serão revogados imediatamente.")) return;
    setBusy("disable");
    try {
      const response = await api.post("/admin/student-mobile-test/disable", { password });
      setStatus(response.data || { enabled: false });
      setPassword("");
      setSelected(null);
      setQuery("");
      setIssued(null);
      toast.success("Modo de teste desligado e sessões temporárias revogadas.");
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setBusy("");
    }
  };

  const generateCode = async () => {
    if (!selected) { toast.error("Escolha o aluno que será usado no teste."); return; }
    if (!requirePassword()) return;
    setBusy("code");
    try {
      const response = await api.post(`/admin/student-mobile-test/code/${selected.id}`, { password });
      setIssued(response.data || {});
      setPassword("");
      toast.success("Código temporário criado.");
      loadStatus({ quiet: true });
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setBusy("");
    }
  };

  const accessCode = issued?.code || issued?.access_code || issued?.test_code || "";
  const codeExpiresAt = issued?.code_expires_at || issued?.expires_at;
  const testExpiresAt = status?.expires_at || status?.enabled_until;
  const formatWhen = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("pt-BR");
  };

  const copyCode = async () => {
    if (!accessCode) return;
    try {
      await navigator.clipboard.writeText(accessCode);
      toast.success("Código copiado. Ele só pode ser usado uma vez.");
    } catch (error) {
      toast.error("Não foi possível copiar automaticamente. Copie o código exibido.");
    }
  };

  const enabled = Boolean(status?.enabled);

  return (
    <Section eyebrow="segurança · app do aluno" title="Acesso temporário de teste">
      <div className="flex flex-col gap-5">
        <div className={`border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${enabled ? "border-amber-500 bg-amber-50/50 dark:bg-amber-950/20" : "border-border bg-muted/20"}`}>
          <div>
            <div className="label-eyebrow mb-1">modo de teste no celular</div>
            <div className={`font-display text-xl font-bold ${enabled ? "text-amber-700 dark:text-amber-300" : ""}`} data-testid="mobile-student-test-status">
               {loadingStatus ? "Consultando…" : enabled ? "ATIVO" : "DESLIGADO"}
            </div>
            {enabled && testExpiresAt && (
              <p className="text-xs text-muted-foreground mt-1">Desliga automaticamente em {formatWhen(testExpiresAt)}.</p>
            )}
          </div>
          <p className="text-sm text-muted-foreground max-w-xl">
             Gera um código único para abrir o app como um aluno escolhido. A sessão é curta, identificada como teste e somente de leitura. Ao desligar, todos os celulares de teste perdem acesso na hora.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-3 items-end">
          <div>
            <div className="label-eyebrow mb-1">sua senha de administrador</div>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
               placeholder="Confirme a senha para uma ação sensível"
              className="rounded-none"
              data-testid="mobile-student-test-password"
            />
          </div>
          {enabled ? (
            <Button variant="destructive" onClick={disable} disabled={busy !== "" || loadingStatus} className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="mobile-student-test-disable">
               {busy === "disable" ? "Revogando…" : "Desligar e revogar"}
            </Button>
          ) : (
            <Button onClick={enable} disabled={busy !== "" || loadingStatus} className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="mobile-student-test-enable">
               {busy === "enable" ? "Ativando…" : "Ativar testes temporários"}
            </Button>
          )}
        </div>

        {enabled && (
          <div className="border border-border p-4" data-testid="mobile-student-test-code-panel">
            <div className="label-eyebrow mb-4">gerar acesso como aluno</div>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-4 items-end">
              <div className="relative">
                <div className="label-eyebrow mb-1">aluno ativo</div>
                {selected ? (
                  <div className="border border-primary p-3 flex items-center justify-between gap-3" data-testid="mobile-student-test-selected-student">
                    <div>
                      <div className="font-mono-data font-bold">{selected.name}</div>
                      <div className="text-xs text-muted-foreground">{selected.email || "sem e-mail cadastrado"}</div>
                      <div className="text-[10px] uppercase tracking-widest text-primary mt-1">aluno ativo</div>
                    </div>
                    <button type="button" className="text-xs text-primary" onClick={() => { setSelected(null); setQuery(""); setIssued(null); }}>trocar</button>
                  </div>
                ) : (
                  <>
                     <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Digite nome, CPF ou e-mail do aluno…" className="rounded-none" data-testid="mobile-student-test-student-search" />
                    {results.length > 0 && (
                      <div className="absolute z-20 left-0 right-0 border border-border bg-card max-h-56 overflow-y-auto shadow-lg">
                        {results.map((item) => (
                          <button key={item.id} type="button" onClick={() => { setSelected(item); setResults([]); }} className="w-full text-left p-3 border-b border-border last:border-0 hover:bg-muted/40">
                            <span className="font-mono-data text-sm font-semibold">{item.name}</span>
                            <span className="block text-xs text-muted-foreground">{item.email || item.cpf || "aluno cadastrado"}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {query.trim().length >= 2 && !results.length && (
                       <p className="text-xs text-muted-foreground mt-2">A busca mostra apenas alunos ativos. Se não aparecer, confira nome, CPF ou e-mail.</p>
                    )}
                  </>
                )}
              </div>
              <Button onClick={generateCode} disabled={!selected || busy !== ""} className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="mobile-student-test-generate-code">
                 {busy === "code" ? "Criando código…" : "Gerar código único"}
              </Button>
            </div>

            {accessCode && (
              <div className="mt-5 border border-amber-500 bg-amber-50/70 dark:bg-amber-950/20 p-4" data-testid="mobile-student-test-issued-code">
                 <div className="label-eyebrow mb-2">código de teste · use uma vez no app</div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <code className="font-mono-data text-2xl font-bold tracking-[0.18em] select-all break-all">{accessCode}</code>
                  <Button type="button" variant="outline" size="sm" onClick={copyCode} className="rounded-none font-mono-data uppercase tracking-widest text-[10px] w-fit" data-testid="mobile-student-test-copy-code">Copiar</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                   Entregue-o somente para você no iPhone de teste. {codeExpiresAt ? `Expira em ${formatWhen(codeExpiresAt)}. ` : ""}Depois de usado, ele não funciona novamente.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Section>
  );
}

/* ---------- Admin: Schools ---------- */
function SchoolsBlock({ onChange }) {
  const [schools, setSchools] = useState([]);
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  const load = async () => setSchools((await api.get("/schools")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/schools", { name });
      toast.success("Escola criada");
      setName(""); setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(fmtError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Remover escola? Isso apaga todas as turmas dela.")) return;
    try { await api.delete(`/schools/${id}`); toast.success("Removida"); load(); onChange?.(); }
    catch (e) { toast.error(fmtError(e)); }
  };

  return (
    <Section
      eyebrow="hierarquia · escolas"
      title="Escolas"
      right={user?.is_admin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="new-school-btn">
              <Plus size={14} weight="bold" className="mr-2" /> Nova
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none" aria-describedby="new-school-desc">
            <DialogHeader><DialogTitle>Nova escola</DialogTitle></DialogHeader>
            <p id="new-school-desc" className="sr-only">Cadastro de escola</p>
            <Input data-testid="new-school-name" value={name} onChange={e => setName(e.target.value)}
              placeholder="Nome da escola" className="rounded-none" />
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button className="rounded-none" onClick={create} data-testid="new-school-save">Criar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    >
      {schools.length === 0 ? (
        <div className="label-eyebrow text-center py-8">nenhuma escola</div>
      ) : (
        <div className="divide-y divide-border">
          {schools.map(s => (
            <div key={s.id} data-testid={`school-row-${s.id}`} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Buildings size={20} weight="duotone" className="text-primary" />
                <span className="font-mono-data">{s.name}{s.kind === "personal" && <span className="block text-[10px] uppercase tracking-widest text-primary">espaco pessoal</span>}</span>
              </div>
              {user?.is_admin && s.kind !== "personal" && (
                <button onClick={() => del(s.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ---------- Admin: Semesters ---------- */
function SemestersBlock({ capabilities }) {
  const [items, setItems] = useState([]);
  const [schools, setSchools] = useState([]);
  const [name, setName] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [open, setOpen] = useState(false);
  const managedSchoolIds = new Set(capabilities?.managed_school_ids || []);
  const canManage = Boolean(capabilities?.is_admin || managedSchoolIds.size);
  const manageableSchools = schools.filter((item) => capabilities?.is_admin || managedSchoolIds.has(item.id));

  const load = async () => {
    const [semesterResponse, schoolResponse] = await Promise.all([
      api.get("/semesters"), api.get("/schools"),
    ]);
    setItems(semesterResponse.data || []);
    setSchools(schoolResponse.data || []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (open && !manageableSchools.some((item) => item.id === schoolId)) {
      setSchoolId(manageableSchools[0]?.id || "");
    }
  }, [open, schoolId, schools, capabilities]);

  const create = async () => {
    try { await api.post("/semesters", { name, school_id: schoolId }); setName(""); setSchoolId(""); setOpen(false); load(); toast.success("Semestre criado"); }
    catch (e) { toast.error(fmtError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/semesters/${id}`); load(); toast.success("Removido"); }
    catch (e) { toast.error(fmtError(e)); }
  };

  return (
    <Section
      eyebrow="dimensão temporal"
      title="Semestres"
      right={false && canManage && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="new-semester-btn">
              <Plus size={14} weight="bold" className="mr-2" /> Novo
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none" aria-describedby="new-sem-desc">
            <DialogHeader><DialogTitle>Novo semestre</DialogTitle></DialogHeader>
            <p id="new-sem-desc" className="sr-only">Cadastro semestre</p>
            <div className="space-y-3">
              <div>
                <div className="label-eyebrow mb-1">escola</div>
                <Select value={schoolId} onValueChange={setSchoolId}>
                  <SelectTrigger data-testid="new-semester-school" className="rounded-none"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {manageableSchools.map((item) => (
                      <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="Ex: 2026.1" value={name} onChange={e => setName(e.target.value)}
                data-testid="new-semester-name" className="rounded-none" />
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button className="rounded-none" disabled={!schoolId || !name.trim()} onClick={create} data-testid="new-semester-save">Criar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    >
      <p className="text-sm text-muted-foreground mb-4">
        Os semestres são globais e aparecem em todas as escolas. O sistema mantém automaticamente o semestre atual e mais cinco futuros; os passados permanecem disponíveis como histórico.
      </p>
      {items.length === 0 ? (
        <div className="label-eyebrow text-center py-8">nenhum semestre</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map(s => (
            <div key={s.id} data-testid={`sem-chip-${s.id}`}
              className="flex items-center gap-2 border border-border px-3 py-2">
              <CalendarBlank size={14} weight="duotone" className="text-primary" />
              <span>
                <span className="font-mono-data font-semibold">{s.name}</span>
                <span className="block text-[10px] text-muted-foreground">
                  global · todas as escolas{s.auto_generated ? " · automático" : ""}{s.is_current ? " · atual" : s.semester_status === "future" ? " · futuro" : s.semester_status === "history" ? " · histórico" : ""}
                </span>
              </span>
              {!s.auto_generated && (capabilities?.is_admin || managedSchoolIds.has(s.school_id)) && (
                <button onClick={() => del(s.id)} className="text-muted-foreground hover:text-destructive ml-2">
                  <Trash size={12} weight="bold" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ---------- Classes ---------- */
function ClassesBlock({ capabilities }) {
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const managedSchoolIds = new Set(capabilities?.managed_school_ids || []);
  const canCreateClass = Boolean(capabilities?.is_admin || managedSchoolIds.size > 0);

  const load = async () => {
    setSchools((await api.get("/schools")).data);
    setClasses((await api.get("/classes")).data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/classes", { name, school_id: schoolId });
      setName(""); setSchoolId(""); setOpen(false); load(); toast.success("Turma criada");
    } catch (e) { toast.error(fmtError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Remover turma?")) return;
    try { await api.delete(`/classes/${id}`); load(); toast.success("Removida"); }
    catch (e) { toast.error(fmtError(e)); }
  };

  const schoolName = (id) => schools.find(s => s.id === id)?.name || "?";

  return (
    <Section
      eyebrow="hierarquia · turmas"
      title="Turmas"
      right={canCreateClass && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="new-class-btn">
              <Plus size={14} weight="bold" className="mr-2" /> Nova
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none" aria-describedby="new-class-desc">
            <DialogHeader><DialogTitle>Nova turma</DialogTitle></DialogHeader>
            <p id="new-class-desc" className="sr-only">Cadastro turma</p>
            <div className="space-y-3">
              <div>
                <div className="label-eyebrow mb-1">escola</div>
                <Select value={schoolId} onValueChange={setSchoolId}>
                  <SelectTrigger data-testid="new-class-school" className="rounded-none">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {schools.filter((s) => capabilities?.is_admin || managedSchoolIds.has(s.id)).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="Nome da turma (ex: T16)" value={name} onChange={e => setName(e.target.value)}
                data-testid="new-class-name" className="rounded-none" />
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button className="rounded-none" onClick={create} disabled={!schoolId || !name} data-testid="new-class-save">
                Criar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    >
      {classes.length === 0 ? (
        <div className="label-eyebrow text-center py-8">nenhuma turma</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {classes.map(c => (
            <div key={c.id} data-testid={`class-card-${c.id}`}
              className="border border-border p-4 hover:border-foreground transition-colors">
              <div className="flex items-start justify-between mb-2">
                <GraduationCap size={20} weight="duotone" className="text-primary" />
                {(capabilities?.is_admin || managedSchoolIds.has(c.school_id)) && (
                  <button onClick={() => del(c.id)} className="text-muted-foreground hover:text-destructive">
                    <Trash size={12} weight="bold" />
                  </button>
                )}
              </div>
              <div className="font-mono-data font-bold">{c.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{schoolName(c.school_id)}</div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ---------- Hierarchical users and roles ---------- */
function MembershipsBlock({ capabilities, allowedRoles = null, title = "Professores e gestores" }) {
  const [q, setQ] = useState("");
  const [foundUsers, setFoundUsers] = useState([]);
  const [mode, setMode] = useState("new");
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newCpf, setNewCpf] = useState("");
  const [role, setRole] = useState("");
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [stages, setStages] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [disciplineIds, setDisciplineIds] = useState([]);
  const [selUser, setSelUser] = useState(null);
  const [schoolId, setSchoolId] = useState("");
  const [stageId, setStageId] = useState("");
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [resendingUserId, setResendingUserId] = useState("");
  const [independent, setIndependent] = useState(false);

  const assignableRoles = (capabilities?.assignable_roles || []).filter(
    (item) => !allowedRoles || allowedRoles.includes(item),
  );

  const load = async () => {
    const [schoolsRes, classesRes, semestersRes, stagesRes, disciplinesRes, rowsRes] = await Promise.all([
      api.get("/schools"),
      api.get("/classes"),
      api.get("/semesters"),
      api.get("/academic-stages"),
      api.get("/disciplines"),
      api.get("/memberships"),
    ]);
    setSchools(schoolsRes.data || []);
    setClasses(classesRes.data || []);
    setSemesters(semestersRes.data || []);
    setStages(stagesRes.data || []);
    setDisciplines(disciplinesRes.data || []);
    setRows(rowsRes.data || []);
  };

  useEffect(() => {
    load().catch((error) => toast.error(fmtError(error)));
  }, []);

  useEffect(() => {
    if (!assignableRoles.length) return;
    if (!assignableRoles.includes(role)) setRole(assignableRoles[0]);
  }, [assignableRoles, role]);

  useEffect(() => {
    setSchoolId("");
    setStageId("");
    setDisciplineIds([]);
    setIndependent(false);
  }, [role]);

  useEffect(() => {
    if (mode !== "existing" || q.length < 2) { setFoundUsers([]); return; }
    const timer = setTimeout(async () => {
      try {
        const response = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
        setFoundUsers(response.data || []);
      } catch {
        setFoundUsers([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [q, mode]);

  const visibleSchoolIds = new Set(capabilities?.visible_school_ids || capabilities?.managed_school_ids || []);
  const availableSchools = capabilities?.is_admin
    ? schools
    : schools.filter((item) => visibleSchoolIds.has(item.id));

  const schoolName = (id) => schools.find((item) => item.id === id)?.name || "—";
  const className = (id) => classes.find((item) => item.id === id)?.name || "—";
  const semesterName = (id) => semesters.find((item) => item.id === id)?.name || "—";
  const stageName = (id) => stages.find((item) => item.id === id)?.name || "—";
  const availableMembershipStages = stages.filter((item) => item.school_id === schoolId);
  const availableProfessorDisciplines = disciplines.filter((item) => item.school_id === schoolId);

  useEffect(() => {
    if (role !== "gestor_etapa") return;
    if (stageId && !availableMembershipStages.some((item) => item.id === stageId)) setStageId("");
  }, [role, schoolId, stageId, stages]);

  const resetForm = () => {
    setSelUser(null);
    setQ("");
    setFoundUsers([]);
    setNewName("");
    setNewEmail("");
    setNewCpf("");
    setSchoolId("");
    setStageId("");
    setDisciplineIds([]);
    setIndependent(false);
  };

  const submit = async () => {
    if (!role) { toast.error("Selecione o papel"); return; }
    if (role === "gestor_escola" && !schoolId) { toast.error("Selecione a escola"); return; }
    if (role === "gestor_etapa" && (!schoolId || !stageId)) {
      toast.error("Selecione a escola e a etapa");
      return;
    }
    if (role === "professor" && !schoolId && !independent) {
      toast.error("Selecione a escola do professor");
      return;
    }
    if (mode === "existing" && !selUser) { toast.error("Selecione um usuário cadastrado"); return; }
    if (mode === "new" && (!newName.trim() || !newEmail.trim())) {
      toast.error("Informe nome e e-mail");
      return;
    }

    const scope = role === "gestor_escola"
      ? { school_id: schoolId }
      : role === "gestor_etapa"
        ? { school_id: schoolId, semester_id: stages.find((item) => item.id === stageId)?.semester_id || null, stage_id: stageId }
        : { school_id: independent ? null : schoolId };
    setSubmitting(true);
    try {
      if (mode === "existing" && role !== "professor") {
        await api.post("/memberships", { user_id: selUser.id, role, ...scope });
        toast.success("Acesso adicionado ao usuário");
      } else {
        const response = await api.post("/managed-users", {
          name: mode === "existing" ? selUser.name : newName.trim(),
          email: mode === "existing" ? selUser.email : newEmail.trim(),
          cpf: mode === "existing" ? null : newCpf,
          role,
          discipline_ids: role === "professor" ? disciplineIds : [],
          ...scope,
        });
        const result = response.data || {};
        if (result.pending_activation) {
          if (result.invitation_sent) {
            toast.success("Usuário adicionado e convite enviado por e-mail");
          } else {
            toast.success("Usuário adicionado. Oriente-o a se cadastrar com o mesmo e-mail");
          }
        } else {
          toast.success("Usuário existente localizado e acesso adicionado");
        }
      }
      resetForm();
      await load();
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este acesso?")) return;
    try {
      await api.delete(`/memberships/${id}`);
      await load();
      toast.success("Acesso removido");
    } catch (error) {
      toast.error(fmtError(error));
    }
  };

  const resendConfirmation = async (targetUser) => {
    if (!targetUser?.id) return;
    if (!window.confirm(`Reenviar a confirmação para ${targetUser.name || targetUser.email}?`)) return;
    setResendingUserId(targetUser.id);
    try {
      const response = await api.post(`/admin/users/${targetUser.id}/resend-confirmation`);
      const kind = response.data?.delivery_kind;
      toast.success(kind === "verification_code"
        ? "Novo código de 6 dígitos enviado"
        : "Convite de confirmação reenviado");
      await load();
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setResendingUserId("");
    }
  };

  const roleLabel = (value) => ({
    gestor_escola: "Gestor da escola",
    gestor_etapa: "Gestor da etapa",
    gestor_turma: "Gestor da turma (legado)",
    professor: "Professor",
  }[value] || value);

  return (
    <Section eyebrow="permissões · hierarquia" title={title}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-border p-4">
          <div className="flex gap-2 mb-4">
            <Button
              variant={mode === "new" ? "default" : "outline"}
              onClick={() => { setMode("new"); resetForm(); }}
              className="rounded-none flex-1 font-mono-data uppercase tracking-widest text-xs"
            >
              Novo usuário
            </Button>
            <Button
              variant={mode === "existing" ? "default" : "outline"}
              onClick={() => { setMode("existing"); resetForm(); }}
              className="rounded-none flex-1 font-mono-data uppercase tracking-widest text-xs"
            >
              Já cadastrado
            </Button>
          </div>

          <div className="space-y-3">
            {mode === "new" ? (
              <>
                <div>
                  <div className="label-eyebrow mb-1">nome</div>
                  <Input value={newName} onChange={(event) => setNewName(event.target.value)}
                    placeholder="Nome completo" className="rounded-none" data-testid="managed-user-name" />
                </div>
                <div>
                  <div className="label-eyebrow mb-1">e-mail</div>
                  <Input type="email" value={newEmail} onChange={(event) => setNewEmail(event.target.value)}
                    placeholder="usuario@escola.com" className="rounded-none" data-testid="managed-user-email" />
                  <div className="text-xs text-muted-foreground mt-2">
                    A pessoa usará esse mesmo e-mail no cadastro para definir a própria senha.
                  </div>
                </div>
                {role === "professor" && (
                  <div>
                    <div className="label-eyebrow mb-1">cpf</div>
                    <Input value={newCpf} onChange={(event) => setNewCpf(event.target.value.replace(/\D/g, "").slice(0, 11))}
                      placeholder="CPF (11 dígitos)" className="rounded-none font-mono-data" data-testid="managed-user-cpf" />
                  </div>
                )}
              </>
            ) : (
              <div>
                <div className="label-eyebrow mb-1">buscar usuário cadastrado</div>
                <Input placeholder="Nome ou e-mail..." value={q}
                  onChange={(event) => setQ(event.target.value)} data-testid="mem-user-search" className="rounded-none font-mono-data" />
                {selUser ? (
                  <div data-testid="mem-selected-user" className="mt-2 border border-primary p-2 flex items-center justify-between">
                    <div className="text-sm">
                      <div className="font-mono-data font-semibold">{selUser.name}</div>
                      <div className="text-xs text-muted-foreground">{selUser.email}</div>
                    </div>
                    <button onClick={() => setSelUser(null)} className="text-xs text-primary">trocar</button>
                  </div>
                ) : foundUsers.length > 0 && (
                  <div className="border border-border mt-1 max-h-48 overflow-y-auto">
                    {foundUsers.map((item) => (
                      <button key={item.id} data-testid={`user-suggestion-${item.id}`}
                        onClick={() => { setSelUser(item); setQ(""); setFoundUsers([]); }}
                        className="w-full text-left px-3 py-2 hover:bg-muted border-b border-border last:border-b-0">
                        <div className="font-mono-data text-sm">{item.name}</div>
                        <div className="text-xs text-muted-foreground">{item.email}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="label-eyebrow mb-1">papel permitido pela sua hierarquia</div>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="mem-role" className="rounded-none"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {assignableRoles.map((item) => (
                    <SelectItem key={item} value={item}>{roleLabel(item)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {capabilities?.is_admin && role === "professor" && (
              <label className="border border-primary/40 bg-primary/5 p-3 flex items-start gap-3 cursor-pointer" data-testid="independent-professor-toggle">
                <input type="checkbox" checked={independent} onChange={(event) => {
                  setIndependent(event.target.checked);
                  if (event.target.checked) { setSchoolId(""); setDisciplineIds([]); }
                }} className="mt-1" />
                <span className="text-sm">
                  <b>Professor independente, sem escola</b>
                  <span className="block text-xs text-muted-foreground mt-1">O sistema cria o espaco pessoal Professor + nome, onde ele pode cadastrar turmas, estrutura, disciplinas e provas.</span>
                </span>
              </label>
            )}

            {(role === "gestor_escola" || role === "gestor_etapa" || (role === "professor" && !independent)) && (
              <div>
                <div className="label-eyebrow mb-1">escola</div>
                <Select value={schoolId} onValueChange={(value) => { setSchoolId(value); setStageId(""); }}>
                  <SelectTrigger data-testid="mem-school" className="rounded-none"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {availableSchools.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {role === "gestor_etapa" ? (
              <>
                <div>
                  <div className="label-eyebrow mb-1">etapa</div>
                  <Select value={stageId} onValueChange={setStageId}>
                    <SelectTrigger data-testid="mem-stage" className="rounded-none"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {availableMembershipStages.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : role === "professor" && !independent ? (
              <div>
                <div className="label-eyebrow mb-2">disciplinas de pré-cadastro (opcional)</div>
                <div className="border border-border max-h-52 overflow-y-auto divide-y divide-border">
                  {availableProfessorDisciplines.map((item) => (
                    <label key={item.id} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30">
                      <input type="checkbox" checked={disciplineIds.includes(item.id)} onChange={() => setDisciplineIds((current) => current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id])} />
                      <span className="text-sm"><b>{item.name}</b><span className="block text-xs text-muted-foreground">{stageName(item.stage_id)}</span></span>
                    </label>
                  ))}
                  {!availableProfessorDisciplines.length && <div className="p-4 text-sm text-muted-foreground">Pode deixar sem disciplina e alocar depois.</div>}
                </div>
                <div className="text-xs text-muted-foreground mt-2">Turma e semestre não são exigidos. A alocação letiva será feita dentro da disciplina.</div>
              </div>
            ) : null}

            <Button data-testid="mem-submit" onClick={submit} disabled={submitting || !role}
              className="w-full rounded-none font-mono-data uppercase tracking-widest text-xs">
              <Plus size={14} weight="bold" className="mr-2" />
              {submitting ? "Adicionando…" : "Adicionar usuário"}
            </Button>
          </div>
        </div>

        <div className="border border-border">
          <div className="label-eyebrow p-4 pb-2">acessos sob sua gestão ({rows.length})</div>
          <div className="divide-y divide-border max-h-[620px] overflow-y-auto">
            {rows.length === 0 ? (
              <div className="p-6 label-eyebrow text-center">nenhum acesso</div>
            ) : rows.filter((item) => !allowedRoles || allowedRoles.includes(item.role)).map((item) => (
              <div key={item.id} data-testid={`membership-row-${item.id}`} className="p-3 flex items-center justify-between gap-3">
                <div className="text-sm min-w-0">
                  <div className="font-mono-data font-semibold truncate">{item.user?.name || item.user_id}</div>
                  <div className="text-xs text-muted-foreground truncate">{item.user?.email}</div>
                  <div className="text-xs text-muted-foreground font-mono-data mt-1">
                    <span className="text-primary uppercase">{roleLabel(item.role)}</span>
                    {item.school_id && <span> · {schoolName(item.school_id)}</span>}
                    {item.stage_id && <span> · {stageName(item.stage_id)}</span>}
                    {item.class_id && <span> · {className(item.class_id)}</span>}
                    {item.semester_id && <span> · {semesterName(item.semester_id)}</span>}
                  </div>
                  {item.user && !item.user.active && (
                    <div className="mt-1 inline-block border border-[hsl(var(--warning))] px-2 py-0.5 text-[10px] uppercase tracking-widest text-[hsl(var(--warning))]">
                      aguardando ativação
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {capabilities?.is_admin && item.user && !item.user.active
                    && (item.role === "professor" || item.user.account_type === "professor") && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={resendingUserId === item.user.id}
                      onClick={() => resendConfirmation(item.user)}
                      className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-[10px]"
                      data-testid={`resend-professor-confirmation-${item.user.id}`}>
                      <EnvelopeSimple size={13} weight="bold" className="mr-1" />
                      {resendingUserId === item.user.id ? "Enviando…" : "Reenviar confirmação"}
                    </Button>
                  )}
                  {item.can_delete && (
                    <button onClick={() => remove(item.id)} title="Remover acesso"
                      className="text-muted-foreground hover:text-destructive shrink-0">
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ---------- Academic stages, disciplines and teachers ---------- */
function AcademicStructureBlock({ capabilities }) {
  const [schools, setSchools] = useState([]);
  const [matrices, setMatrices] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [stages, setStages] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [classes, setClasses] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [matrixId, setMatrixId] = useState("");
  const [semesterId, setSemesterId] = useState("");
  const [matrixEditor, setMatrixEditor] = useState(null);
  const [stageEditor, setStageEditor] = useState(null);
  const [disciplineEditor, setDisciplineEditor] = useState(null);
  const [classEditor, setClassEditor] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [teacherHistory, setTeacherHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [matrixWorking, setMatrixWorking] = useState(false);
  const matrixFileRef = useRef(null);
  const managedSchoolIds = new Set(capabilities?.managed_school_ids || []);
  const visibleSchoolIds = new Set(capabilities?.visible_school_ids || capabilities?.managed_school_ids || []);
  const managedStageIds = new Set(capabilities?.managed_stage_ids || []);

  const load = async () => {
    const [schoolResponse, semesterResponse, stageResponse, disciplineResponse, classResponse] = await Promise.all([
      api.get("/schools"), api.get("/semesters"), api.get("/academic-stages"), api.get("/disciplines"), api.get("/classes"),
    ]);
    setSchools(schoolResponse.data || []);
    setSemesters(semesterResponse.data || []);
    setStages(stageResponse.data || []);
    setDisciplines(disciplineResponse.data || []);
    setClasses(classResponse.data || []);
  };

  useEffect(() => { load().catch((error) => toast.error(fmtError(error))); }, []);

  const loadStructure = async () => {
    if (!schoolId) return;
    const matrixResponse = await api.get(`/curriculum-matrices?school_id=${encodeURIComponent(schoolId)}`);
    const nextMatrices = matrixResponse.data || [];
    setMatrices(nextMatrices);
    const selectedMatrixId = nextMatrices.some((item) => item.id === matrixId) ? matrixId : (nextMatrices.find((item) => item.active)?.id || nextMatrices[0]?.id || "");
    if (selectedMatrixId !== matrixId) setMatrixId(selectedMatrixId);
    if (!selectedMatrixId) { setStages([]); setDisciplines([]); return; }
    const params = new URLSearchParams({ school_id: schoolId, matrix_id: selectedMatrixId });
    if (semesterId) params.set("semester_id", semesterId);
    const [stageResponse, disciplineResponse] = await Promise.all([
      api.get(`/academic-stages?${params}`),
      api.get(`/disciplines?${params}`),
    ]);
    setStages(stageResponse.data || []);
    setDisciplines(disciplineResponse.data || []);
  };

  useEffect(() => {
    loadStructure().catch((error) => toast.error(fmtError(error)));
  }, [schoolId, matrixId, semesterId]);

  const manageableSchools = schools.filter((item) => capabilities?.is_admin || visibleSchoolIds.has(item.id));
  const canManageSelectedSchool = Boolean(capabilities?.is_admin || managedSchoolIds.has(schoolId));
  const schoolSemesters = semesters;
  const matrixStages = stages
    .filter((item) => item.matrix_id === matrixId && (canManageSelectedSchool || managedStageIds.has(item.id)))
    .sort((a, b) => Number(a.position || 1) - Number(b.position || 1));

  useEffect(() => {
    if (manageableSchools.length && !manageableSchools.some((item) => item.id === schoolId)) {
      setSchoolId(manageableSchools[0].id);
    }
  }, [schools, capabilities, schoolId]);

  useEffect(() => {
    const options = semesters;
    const today = new Date();
    const currentName = `${today.getFullYear()}.${today.getMonth() < 6 ? 1 : 2}`;
    if (!options.some((item) => item.id === semesterId)) {
      setSemesterId(options.find((item) => item.name === currentName)?.id || options[0]?.id || "");
    }
  }, [schoolId, semesters, semesterId]);

  const openStage = (item = null) => {
    if (!matrixId) return toast.error("Cadastre e selecione uma matriz curricular primeiro");
    setStageEditor({
      id: item?.id || "", name: item?.name || "",
      position: Number(item?.position || matrixStages.length + 1),
      duration_type: item?.duration_type || "semester",
      external_stage_labels: [...(item?.external_stage_labels || [])].join("; "),
    });
  };

  const saveStage = async () => {
    if (!stageEditor?.name?.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: stageEditor.name.trim(), school_id: schoolId, semester_id: null, matrix_id: matrixId,
        position: Number(stageEditor.position || 1),
        duration_type: stageEditor.duration_type || "semester",
        external_stage_labels: String(stageEditor.external_stage_labels || "").split(/[;,]/).map((value) => value.trim()).filter(Boolean),
      };
      if (stageEditor.id) await api.put(`/academic-stages/${stageEditor.id}`, payload);
      else await api.post("/academic-stages", payload);
      toast.success(stageEditor.id ? "Etapa atualizada" : "Etapa criada");
      setStageEditor(null);
      await loadStructure();
    } catch (error) { toast.error(fmtError(error)); }
    finally { setSaving(false); }
  };

  const removeStage = async (item) => {
    if (!window.confirm(`Remover a etapa “${item.name}”?`)) return;
    try { await api.delete(`/academic-stages/${item.id}`); await loadStructure(); toast.success("Etapa removida"); }
    catch (error) { toast.error(fmtError(error)); }
  };

  const openDiscipline = async (stage, item = null) => {
    try {
      const response = await api.get(`/academic-teachers?school_id=${encodeURIComponent(schoolId)}`);
      setTeachers(response.data || []);
      if (item?.id && semesterId) {
        const history = await api.get(`/disciplines/${item.id}/teacher-history?semester_id=${encodeURIComponent(semesterId)}`);
        setTeacherHistory(history.data?.previous_semesters || []);
      } else {
        setTeacherHistory([]);
      }
      setDisciplineEditor({
        id: item?.id || "", stage_id: stage.id, name: item?.name || "",
        teacher_ids: [...(item?.teacher_ids || [])],
      });
    } catch (error) { toast.error(fmtError(error)); }
  };

  const toggleTeacher = (teacherId) => {
    setDisciplineEditor((current) => ({
      ...current,
      teacher_ids: current.teacher_ids.includes(teacherId)
        ? current.teacher_ids.filter((value) => value !== teacherId)
        : [...current.teacher_ids, teacherId],
    }));
  };

  const saveDiscipline = async () => {
    if (!disciplineEditor?.name?.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: disciplineEditor.name.trim(), stage_id: disciplineEditor.stage_id,
        teacher_ids: [],
      };
      let disciplineId = disciplineEditor.id;
      if (disciplineId) await api.put(`/disciplines/${disciplineId}`, payload);
      else disciplineId = (await api.post("/disciplines", payload)).data.id;
      if (semesterId) {
        await api.put(`/disciplines/${disciplineId}/offerings/${semesterId}`, {
          teacher_ids: disciplineEditor.teacher_ids,
        });
      }
      toast.success(disciplineEditor.id ? "Disciplina atualizada" : "Disciplina criada");
      setDisciplineEditor(null);
      await loadStructure();
    } catch (error) { toast.error(fmtError(error)); }
    finally { setSaving(false); }
  };

  const removeDiscipline = async (item) => {
    if (!window.confirm(`Remover a disciplina “${item.name}”?`)) return;
    try { await api.delete(`/disciplines/${item.id}`); await loadStructure(); toast.success("Disciplina removida"); }
    catch (error) { toast.error(fmtError(error)); }
  };

  const openClasses = (stage) => {
    setClassEditor({
      stage_id: stage.id,
      stage_name: stage.name,
      class_ids: [...(stage.class_ids || [])],
    });
  };

  const toggleClass = (classIdValue) => {
    setClassEditor((current) => ({
      ...current,
      class_ids: current.class_ids.includes(classIdValue)
        ? current.class_ids.filter((value) => value !== classIdValue)
        : [...current.class_ids, classIdValue],
    }));
  };

  const saveClasses = async () => {
    setSaving(true);
    try {
      await api.put(`/academic-stages/${classEditor.stage_id}/classes`, {
        class_ids: classEditor.class_ids,
        semester_id: semesterId,
      });
      toast.success("Turmas da etapa atualizadas");
      setClassEditor(null);
      await loadStructure();
    } catch (error) { toast.error(fmtError(error)); }
    finally { setSaving(false); }
  };

  const saveMatrix = async () => {
    if (!matrixEditor?.name?.trim() || !schoolId) return;
    setSaving(true);
    try {
      const payload = { name: matrixEditor.name.trim(), school_id: schoolId, active: matrixEditor.active !== false };
      let saved;
      if (matrixEditor.id) saved = (await api.put(`/curriculum-matrices/${matrixEditor.id}`, payload)).data;
      else saved = (await api.post("/curriculum-matrices", payload)).data;
      setMatrixId(saved.id);
      setMatrixEditor(null);
      await loadStructure();
      toast.success(matrixEditor.id ? "Matriz atualizada" : "Matriz curricular criada");
    } catch (error) { toast.error(fmtError(error)); }
    finally { setSaving(false); }
  };

  const downloadMatrix = async () => {
    if (!schoolId) return;
    setMatrixWorking(true);
    try {
      const params = new URLSearchParams({ school_id: schoolId });
      if (matrixId) params.set("matrix_id", matrixId);
      const response = await api.get(`/admin/curriculum-matrices/template.xlsx?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "matriz_curricular.xlsx";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error(fmtError(error)); }
    finally { setMatrixWorking(false); }
  };

  const uploadMatrix = async (file) => {
    if (!file || !schoolId) return;
    setMatrixWorking(true);
    try {
      const body = new FormData();
      body.append("school_id", schoolId);
      body.append("file", file);
      const response = await api.post("/admin/curriculum-matrices/import", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMatrixId(response.data.matrix.id);
      await loadStructure();
      toast.success(`${response.data.stage_count} unidade(s) e ${response.data.discipline_count} disciplina(s) importadas`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.errors?.length) detail.errors.slice(0, 8).forEach((message) => toast.error(message));
      else toast.error(fmtError(error));
    } finally {
      setMatrixWorking(false);
      if (matrixFileRef.current) matrixFileRef.current.value = "";
    }
  };

  return (
    <Section
      eyebrow="organização pedagógica"
      title="Matriz curricular, etapas e disciplinas"
      right={<div className="flex flex-wrap gap-2">
        {capabilities?.is_admin && schoolId && <>
          <Button variant="outline" disabled={matrixWorking} className="rounded-none font-mono-data uppercase tracking-widest text-xs" onClick={downloadMatrix} data-testid="matrix-download"><DownloadSimple size={14} className="mr-2" /> Baixar XLSX</Button>
          <Button variant="outline" disabled={matrixWorking} className="rounded-none font-mono-data uppercase tracking-widest text-xs" onClick={() => matrixFileRef.current?.click()} data-testid="matrix-upload"><UploadSimple size={14} className="mr-2" /> Importar XLSX</Button>
          <input ref={matrixFileRef} type="file" accept=".xlsx" className="hidden" onChange={(event) => uploadMatrix(event.target.files?.[0])} />
        </>}
        {matrixId && canManageSelectedSchool && <Button className="rounded-none font-mono-data uppercase tracking-widest text-xs" onClick={() => openStage()} data-testid="new-stage-btn"><Plus size={14} className="mr-2" /> Nova etapa</Button>}
      </div>}
    >
      <p className="text-sm text-muted-foreground mb-5 max-w-4xl">
        Organize Escola → Matriz Curricular → Etapas → Disciplinas. O semestre letivo serve para alocar turmas e professores; versões anteriores da matriz permanecem no histórico. Professores não são copiados automaticamente: ao editar a disciplina no semestre seguinte, você pode copiar todos ou selecionar docentes de qualquer um dos três semestres anteriores.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div>
          <div className="label-eyebrow mb-1">escola</div>
          <Select value={schoolId} onValueChange={(value) => { setSchoolId(value); setMatrixId(""); }}>
            <SelectTrigger className="rounded-none" data-testid="structure-school"><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>{manageableSchools.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-eyebrow mb-1">matriz curricular</div>
          <div className="flex gap-2">
            <Select value={matrixId} onValueChange={setMatrixId}>
              <SelectTrigger className="rounded-none" data-testid="structure-matrix"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>{matrices.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}{!item.active ? " · histórica" : ""}</SelectItem>)}</SelectContent>
            </Select>
            {canManageSelectedSchool && <Button variant="outline" className="rounded-none px-3" onClick={() => setMatrixEditor({ name: "", active: true })} title="Nova matriz"><Plus size={15} /></Button>}
            {canManageSelectedSchool && matrixId && <Button variant="outline" className="rounded-none px-3" onClick={() => setMatrixEditor(matrices.find((item) => item.id === matrixId))} title="Editar matriz"><GraduationCap size={15} /></Button>}
          </div>
        </div>
        <div>
          <div className="label-eyebrow mb-1">semestre letivo</div>
          <Select value={semesterId} onValueChange={setSemesterId}>
            <SelectTrigger className="rounded-none" data-testid="structure-semester"><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>{schoolSemesters.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!matrixId ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Cadastre ou selecione uma matriz curricular.</div>
      ) : !matrixStages.length ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Nenhuma etapa cadastrada nesta matriz.</div>
      ) : (
        <div className="space-y-4">
          {matrixStages.map((stage) => {
            const stageDisciplines = disciplines.filter((item) => item.stage_id === stage.id);
            return (
              <article key={stage.id} className="border border-border" data-testid={`stage-card-${stage.id}`}>
                <div className="p-4 bg-muted/20 border-b border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="label-eyebrow">etapa {stage.position}</div>
                    <div className="font-display text-2xl font-black">{stage.name}</div>
                    <div className="flex flex-wrap gap-2 mt-2 text-[10px] font-mono-data uppercase tracking-widest">
                      <span className="border border-primary/40 px-2 py-1 text-primary">{stage.duration_type === "annual" ? "anual" : "semestral"}</span>
                      {!!stage.external_stage_labels?.length && <span className="border border-border px-2 py-1 text-muted-foreground">equivale a {stage.external_stage_labels.join(" / ")}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {(stage.class_ids || []).length
                        ? (stage.class_ids || []).map((id) => classes.find((item) => item.id === id)?.name || id).join(" · ")
                        : "Nenhuma turma atribuída nesta etapa"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" className="rounded-none" onClick={() => openClasses(stage)}>Turmas</Button>
                    <Button variant="outline" className="rounded-none" onClick={() => openDiscipline(stage)}><Plus size={14} className="mr-2" /> Disciplina</Button>
                    <Button variant="outline" className="rounded-none" onClick={() => openStage(stage)}>Editar etapa</Button>
                    <Button variant="outline" className="rounded-none text-destructive" onClick={() => removeStage(stage)}><Trash size={14} /></Button>
                  </div>
                </div>
                {stageDisciplines.length ? (
                  <div className="divide-y divide-border">
                    {stageDisciplines.map((discipline) => (
                      <div key={discipline.id} className="p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3" data-testid={`discipline-row-${discipline.id}`}>
                        <div>
                          <div className="font-mono-data font-bold">{discipline.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {(discipline.teachers || []).length
                              ? (discipline.teachers || []).map((teacher) => teacher.name || teacher.email).join(" · ")
                              : "Nenhum professor atribuído"}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" className="rounded-none" onClick={() => openDiscipline(stage, discipline)}>Editar professores</Button>
                          <Button variant="outline" className="rounded-none text-destructive" onClick={() => removeDiscipline(discipline)}><Trash size={14} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="p-5 text-sm text-muted-foreground">Nenhuma disciplina nesta etapa. Provas globais, como simulados, ainda podem ser vinculadas diretamente à etapa.</div>}
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={Boolean(matrixEditor)} onOpenChange={(open) => !open && setMatrixEditor(null)}>
        <DialogContent className="rounded-none" aria-describedby="matrix-editor-desc">
          <DialogHeader><DialogTitle>{matrixEditor?.id ? "Editar matriz curricular" : "Nova matriz curricular"}</DialogTitle></DialogHeader>
          <p id="matrix-editor-desc" className="text-sm text-muted-foreground">Crie uma nova versão quando a organização das etapas e disciplinas mudar.</p>
          <Input value={matrixEditor?.name || ""} onChange={(event) => setMatrixEditor((current) => ({ ...current, name: event.target.value }))}
            placeholder="Ex.: Matriz Medicina 2026" className="rounded-none" data-testid="matrix-name" />
          {matrixEditor?.id && <label className="flex items-center gap-2 text-sm border border-border p-3">
            <input type="checkbox" checked={matrixEditor.active !== false} onChange={(event) => setMatrixEditor((current) => ({ ...current, active: event.target.checked }))} />
            Matriz ativa para novos planejamentos
          </label>}
          <DialogFooter><Button variant="outline" className="rounded-none" onClick={() => setMatrixEditor(null)}>Cancelar</Button><Button className="rounded-none" onClick={saveMatrix} disabled={saving || !matrixEditor?.name?.trim()}>{saving ? "Salvando…" : "Salvar matriz"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(classEditor)} onOpenChange={(open) => !open && setClassEditor(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="stage-classes-desc">
          <DialogHeader><DialogTitle>Turmas da etapa {classEditor?.stage_name}</DialogTitle></DialogHeader>
          <p id="stage-classes-desc" className="text-sm text-muted-foreground">
            Se uma turma já estiver em outra etapa deste mesmo semestre, ela será transferida para esta etapa. O vínculo vale somente para o semestre selecionado.
          </p>
          <div className="border border-border max-h-80 overflow-y-auto divide-y divide-border">
            {classes.filter((item) => item.school_id === schoolId).length ? classes.filter((item) => item.school_id === schoolId).map((item) => (
              <label key={item.id} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30">
                <input type="checkbox" checked={classEditor?.class_ids?.includes(item.id) || false} onChange={() => toggleClass(item.id)} />
                <span className="font-mono-data text-sm font-semibold">{item.name}</span>
              </label>
            )) : <div className="p-5 text-sm text-muted-foreground">Nenhuma turma cadastrada nesta escola.</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setClassEditor(null)}>Cancelar</Button>
            <Button className="rounded-none" onClick={saveClasses} disabled={saving}>{saving ? "Salvando…" : "Salvar turmas"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(stageEditor)} onOpenChange={(open) => !open && setStageEditor(null)}>
        <DialogContent className="rounded-none" aria-describedby="stage-editor-desc">
          <DialogHeader><DialogTitle>{stageEditor?.id ? "Editar etapa" : "Nova etapa"}</DialogTitle></DialogHeader>
          <p id="stage-editor-desc" className="text-sm text-muted-foreground">Use qualquer organização, como “1ª etapa”, “2º bimestre” ou “1º semestre”.</p>
          <div className="space-y-3">
            <Input value={stageEditor?.name || ""} onChange={(event) => setStageEditor((current) => ({ ...current, name: event.target.value }))} placeholder="Nome da etapa" className="rounded-none" data-testid="stage-name" />
            <div><div className="label-eyebrow mb-1">ordem</div><Input type="number" min={1} max={50} value={stageEditor?.position || 1} onChange={(event) => setStageEditor((current) => ({ ...current, position: event.target.value }))} className="rounded-none" /></div>
            <div><div className="label-eyebrow mb-1">periodicidade</div>
              <Select value={stageEditor?.duration_type || "semester"} onValueChange={(value) => setStageEditor((current) => ({ ...current, duration_type: value }))}>
                <SelectTrigger className="rounded-none" data-testid="stage-duration"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="semester">Semestral</SelectItem><SelectItem value="annual">Anual</SelectItem></SelectContent>
              </Select>
            </div>
            <div><div className="label-eyebrow mb-1">etapas equivalentes externas</div>
              <Input value={stageEditor?.external_stage_labels || ""} onChange={(event) => setStageEditor((current) => ({ ...current, external_stage_labels: event.target.value }))} placeholder="Ex.: IX; X" className="rounded-none" data-testid="stage-aliases" />
              <div className="text-xs text-muted-foreground mt-1">No internato anual, use IX; X para o 5º ano e XI; XII para o 6º ano.</div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-none" onClick={() => setStageEditor(null)}>Cancelar</Button><Button className="rounded-none" onClick={saveStage} disabled={saving || !stageEditor?.name?.trim()}>{saving ? "Salvando…" : "Salvar etapa"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(disciplineEditor)} onOpenChange={(open) => !open && setDisciplineEditor(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="discipline-editor-desc">
          <DialogHeader><DialogTitle>{disciplineEditor?.id ? "Editar disciplina" : "Nova disciplina"}</DialogTitle></DialogHeader>
          <p id="discipline-editor-desc" className="text-sm text-muted-foreground">Uma disciplina pode possuir vários professores simultaneamente.</p>
          <Input value={disciplineEditor?.name || ""} onChange={(event) => setDisciplineEditor((current) => ({ ...current, name: event.target.value }))} placeholder="Nome da disciplina" className="rounded-none" data-testid="discipline-name" />
          {!!teacherHistory.length && (
            <div className="border border-primary/30 bg-primary/5 p-3">
              <div className="label-eyebrow mb-2">copiar dos últimos 3 semestres</div>
              <div className="space-y-2">
                {teacherHistory.map((semester) => (
                  <div key={semester.id} className="border border-border bg-card p-3">
                    <div className="flex items-center justify-between gap-3">
                      <b className="font-mono-data text-sm">{semester.name}</b>
                      {!!semester.teacher_ids?.length && <button type="button" className="text-xs text-primary" onClick={() => setDisciplineEditor((current) => ({ ...current, teacher_ids: [...new Set([...current.teacher_ids, ...semester.teacher_ids])] }))}>copiar todos</button>}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(semester.teachers || []).map((teacher) => (
                        <label key={teacher.id} className="text-xs border border-border px-2 py-1 cursor-pointer">
                          <input type="checkbox" className="mr-1" checked={disciplineEditor?.teacher_ids?.includes(teacher.id) || false} onChange={() => toggleTeacher(teacher.id)} />
                          {teacher.name}
                        </label>
                      ))}
                      {!semester.teacher_ids?.length && <span className="text-xs text-muted-foreground">Sem professores alocados.</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="label-eyebrow mb-2">professores</div>
            <div className="border border-border max-h-72 overflow-y-auto divide-y divide-border">
              {teachers.length ? teachers.map((teacher) => (
                <label key={teacher.id} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30">
                  <input type="checkbox" checked={disciplineEditor?.teacher_ids?.includes(teacher.id) || false} onChange={() => toggleTeacher(teacher.id)} />
                  <span><span className="font-mono-data text-sm font-semibold">{teacher.name}</span><span className="block text-xs text-muted-foreground">{teacher.email}{!teacher.active ? " · aguardando ativação" : ""}</span></span>
                </label>
              )) : <div className="p-5 text-sm text-muted-foreground">Nenhum professor pré-cadastrado nesta escola. Cadastre acima ou importe o XLSX de professores.</div>}
            </div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-none" onClick={() => setDisciplineEditor(null)}>Cancelar</Button><Button className="rounded-none" onClick={saveDiscipline} disabled={saving || !disciplineEditor?.name?.trim()}>{saving ? "Salvando…" : "Salvar disciplina"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

/* ---------- Admin-only professor pre-registration ---------- */
function ProfessorImportBlock() {
  const fileRef = useRef(null);
  const [working, setWorking] = useState(false);
  const [errors, setErrors] = useState([]);

  const download = async () => {
    setWorking(true);
    try {
      const response = await api.get("/admin/professors/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "template_pre_cadastro_professores.xlsx";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setWorking(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setWorking(true);
    setErrors([]);
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await api.post("/admin/professors/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${response.data.imported} professor(es) importado(s) · ${response.data.invitations_sent} convite(s) enviado(s)`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) setErrors(detail.errors);
      toast.error(typeof detail?.message === "string" ? detail.message : fmtError(error));
    } finally {
      setWorking(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Section eyebrow="administração · professores" title="Pré-cadastro de professores por XLSX">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        <div className="max-w-3xl text-sm text-muted-foreground">
          Importe nome, CPF, e-mail, escola e disciplinas opcionais. O professor pode ficar sem disciplina; turma e semestre não são exigidos no pré-cadastro.
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={download} disabled={working} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
            <DownloadSimple size={14} className="mr-2" /> Baixar modelo
          </Button>
          <Button onClick={() => fileRef.current?.click()} disabled={working} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
            <UploadSimple size={14} className="mr-2" /> {working ? "Processando…" : "Importar preenchido"}
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(event) => upload(event.target.files?.[0])} />
        </div>
      </div>
      {!!errors.length && <div className="mt-4 border border-destructive/50 bg-destructive/5 p-4 max-h-60 overflow-y-auto text-sm font-mono-data">
        {errors.map((item, index) => <div key={`${index}-${item}`}>{item}</div>)}
      </div>}
    </Section>
  );
}

/* ---------- Admin-only bulk question import ---------- */
function LayoutAccessBlock() {
  const [layouts, setLayouts] = useState([]);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLayouts, setSelectedLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/layout-access");
      setLayouts(response.data.layouts || []);
      setUsers((response.data.users || []).filter(item => !item.student_id && item.account_type !== "student"));
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const chooseUser = (item) => {
    setSelectedUser(item);
    setSelectedLayouts((item.allowed_layout_ids || []).filter((id) =>
      !layouts.find((layout) => layout.id === id)?.is_default
    ));
  };

  const toggle = (layoutId) => {
    setSelectedLayouts((current) => current.includes(layoutId)
      ? current.filter((id) => id !== layoutId)
      : [...current, layoutId]);
  };

  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const response = await api.put(`/admin/layout-access/${selectedUser.id}`, {
        allowed_layout_ids: selectedLayouts,
      });
      const effective = response.data.allowed_layout_ids || [];
      setUsers((current) => current.map((item) => item.id === selectedUser.id
        ? { ...item, allowed_layout_ids: effective }
        : item));
      setSelectedUser((current) => ({ ...current, allowed_layout_ids: effective }));
      toast.success("Perfis de gabarito atualizados");
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setSaving(false);
    }
  };

  const visibleUsers = users.filter((item) => {
    const term = query.trim().toLocaleLowerCase("pt-BR");
    return !term || `${item.name} ${item.email}`.toLocaleLowerCase("pt-BR").includes(term);
  });
  const defaultLayout = layouts.find((item) => item.is_default);
  const optionalLayouts = layouts.filter((item) => !item.is_default);

  return (
    <Section eyebrow="admin geral · área privada" title="Gabaritos liberados para gestores e professores">
      <p className="text-sm text-muted-foreground mb-5 max-w-4xl">
        Concessão direta, exclusiva do administrador geral, para gestores e professores. Alunos não podem receber presets. O cartão SAKI/Xm Oficial A4 é padrão para docentes; administradores têm todos os perfis. A delegação por escola é configurada abaixo.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(320px,0.9fr)_minmax(420px,1.1fr)] gap-5">
        <div className="border border-border">
          <div className="p-3 border-b border-border">
            <Input value={query} onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar nome ou e-mail..." className="rounded-none" data-testid="layout-user-search" />
          </div>
          <div className="max-h-[430px] overflow-y-auto divide-y divide-border">
            {loading ? <div className="p-5 label-eyebrow">carregando…</div> : visibleUsers.map((item) => (
              <button key={item.id} type="button" disabled={saving} onClick={() => chooseUser(item)}
                data-testid={`layout-user-${item.id}`}
                className={`w-full text-left p-3 hover:bg-muted/50 ${selectedUser?.id === item.id ? "bg-primary/5 border-l-2 border-primary" : ""}`}>
                <div className="font-mono-data text-sm font-semibold">{item.name}</div>
                <div className="text-xs text-muted-foreground">{item.email}</div>
                <div className="text-[10px] uppercase tracking-widest text-primary mt-1">
                  {item.is_admin ? "admin · todos os perfis" : `${Math.max(0, (item.allowed_layout_ids || []).length - 1)} adicional(is)`}
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="border border-border p-5">
          {!selectedUser ? (
            <div className="label-eyebrow text-center py-16">selecione um usuário</div>
          ) : (
            <>
              <div className="font-display text-2xl font-bold">{selectedUser.name}</div>
              <div className="text-xs text-muted-foreground mb-5">{selectedUser.email}</div>
              <label className="flex items-start gap-3 border border-primary/40 bg-primary/5 p-3 mb-2">
                <input type="checkbox" checked disabled className="mt-1" />
                <span><b>{defaultLayout?.name || "SAKI/Xm Oficial A4 · 100 questões"}</b><br /><span className="text-xs text-muted-foreground">Padrão oficial disponível para gestores e professores.</span></span>
              </label>
              <div className="space-y-2">
                {optionalLayouts.map((layout) => (
                  <label key={layout.id} className="flex items-start gap-3 border border-border p-3 cursor-pointer hover:border-primary/50">
                    <input type="checkbox" className="mt-1" checked={selectedUser.is_admin || selectedLayouts.includes(layout.id)}
                      disabled={selectedUser.is_admin || saving} onChange={() => toggle(layout.id)} />
                    <span><b>{layout.name}</b><br /><span className="text-xs text-muted-foreground">{layout.description}</span></span>
                  </label>
                ))}
              </div>
              <Button onClick={save} disabled={selectedUser.is_admin || saving}
                data-testid="layout-access-save" className="rounded-none mt-5 w-full font-mono-data uppercase tracking-widest text-xs">
                {saving ? "Salvando…" : "Salvar perfis do usuário"}
              </Button>
            </>
          )}
        </div>
      </div>
    </Section>
  );
}

/* ---------- Admin-only bulk question import ---------- */
function ExamQuestionImportBlock() {
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await api.get("/admin/exam-questions/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "modelo_importacao_questoes_provas.xlsx";
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success("Modelo gerado com as provas disponíveis");
    } catch (error) {
      toast.error("Falha ao baixar o modelo: " + fmtError(error));
    } finally {
      setDownloading(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    setErrors([]);
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await api.post("/admin/exam-questions/import", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(response.data);
      toast.success(`${response.data.question_count} objetiva(s), ${response.data.priority_question_count || 0} prioritária(s), ${response.data.annulled_question_count || 0} anulada(s) e ${response.data.perception_question_count || 0} pergunta(s) de percepção importadas em ${response.data.exam_count} prova(s)`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        setErrors(detail.errors);
        toast.error(`${detail.error_count || detail.errors.length} erro(s): nenhuma prova foi alterada`);
      } else {
        toast.error("Falha na importação: " + fmtError(error));
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Section eyebrow="administração · banco de questões" title="Importar questões para provas existentes">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5 items-start">
        <div>
          <div className="flex items-start gap-3">
            <FileXls size={28} weight="duotone" className="text-primary shrink-0" />
            <div>
              <div className="font-mono-data font-bold">Um único XLSX para várias provas</div>
              <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
                O modelo já contém as provas disponíveis, números das questões, gabaritos atuais, a coluna <b>anulada</b>, a coluna <b>prioridade_sorteio</b> e uma aba <b>Percepcao</b>. Marque <b>SIM</b> em prioridade_sorteio para a questão receber peso 4× na montagem automática; ela continua sem ser obrigatória. Planilhas antigas sem essa coluna continuam aceitas.
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={downloadTemplate}
            disabled={downloading || uploading}
            data-testid="admin-question-template-btn"
            className="rounded-none font-mono-data uppercase tracking-widest text-xs"
          >
            <DownloadSimple size={14} weight="bold" className="mr-2" />
            {downloading ? "Gerando…" : "Baixar modelo"}
          </Button>
        </div>
      </div>

      <QuestionSpreadsheetPicker
        inputId="admin-question-import-input"
        inputRef={fileRef}
        disabled={downloading || uploading}
        onFile={upload}
        buttonTestId="admin-question-import-btn"
        dropzoneTestId="admin-question-import-dropzone"
        label={uploading ? "Validando e importando…" : "Enviar questões para as provas"}
      />

      {result && (
        <div className="mt-5 border border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/5 p-4" data-testid="admin-question-import-result">
          <div className="font-mono-data font-bold text-[hsl(var(--success))]">Importação concluída</div>
          <div className="text-sm mt-1">
            {result.exam_count} prova(s) · {result.question_count} objetiva(s) · {result.priority_question_count || 0} prioritária(s) com peso 4× · {result.annulled_question_count || 0} anulada(s) · {result.perception_question_count || 0} pergunta(s) de percepção · {result.created} criada(s) · {result.updated} atualizada(s)
          </div>
        </div>
      )}

      {!!errors.length && (
        <div className="mt-5 border border-destructive/50 bg-destructive/5 p-4" data-testid="admin-question-import-errors">
          <div className="font-mono-data font-bold text-destructive mb-2">Arquivo rejeitado — corrija as linhas abaixo</div>
          <div className="max-h-64 overflow-y-auto space-y-1 text-sm font-mono-data">
            {errors.slice(0, 100).map((error, index) => <div key={`${index}-${error}`}>{error}</div>)}
            {errors.length > 100 && <div>… e mais {errors.length - 100} erro(s)</div>}
          </div>
        </div>
      )}
    </Section>
  );
}

function StandaloneQuestionImportBlock() {
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await api.get("/admin/question-bank-import/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "modelo_importacao_questoes_avulsas.xlsx";
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success("Modelo de questões avulsas gerado");
    } catch (error) {
      toast.error("Falha ao baixar o modelo: " + fmtError(error));
    } finally {
      setDownloading(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    setErrors([]);
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await api.post("/admin/question-bank-import", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(response.data);
      toast.success(`${response.data.question_count} questão(ões) avulsa(s) importada(s); ${response.data.priority_question_count || 0} com peso 4×`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        setErrors(detail.errors);
        toast.error(`${detail.error_count || detail.errors.length} erro(s): nenhuma questão foi alterada`);
      } else {
        toast.error("Falha na importação: " + fmtError(error));
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Section eyebrow="administração · banco de questões" title="Importar questões avulsas para o banco">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5 items-start">
        <div className="flex items-start gap-3">
          <FileXls size={28} weight="duotone" className="text-primary shrink-0" />
          <div>
            <div className="font-mono-data font-bold">Sem vínculo obrigatório com prova</div>
            <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
              Cada linha vira uma questão disponível no banco. Use <b>codigo_externo</b> para atualizar a mesma questão em reimportações e marque <b>SIM</b> em <b>prioridade_sorteio</b> para peso 4× nas montagens automáticas. A questão prioritária continua sem ser obrigatória.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadTemplate} disabled={downloading || uploading}
            data-testid="admin-standalone-question-template-btn" className="rounded-none font-mono-data uppercase tracking-widest text-xs">
            <DownloadSimple size={14} weight="bold" className="mr-2" />
            {downloading ? "Gerando…" : "Baixar modelo avulso"}
          </Button>
        </div>
      </div>
      <QuestionSpreadsheetPicker
        inputId="admin-standalone-question-import-input"
        inputRef={fileRef}
        disabled={downloading || uploading}
        onFile={upload}
        buttonTestId="admin-standalone-question-import-btn"
        dropzoneTestId="admin-standalone-question-import-dropzone"
        label={uploading ? "Validando e importando…" : "Enviar questões avulsas"}
      />
      {result && (
        <div className="mt-5 border border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/5 p-4" data-testid="admin-standalone-question-import-result">
          <div className="font-mono-data font-bold text-[hsl(var(--success))]">Importação concluída</div>
          <div className="text-sm mt-1">{result.question_count} questão(ões) sem prova · {result.priority_question_count || 0} prioritária(s) com peso 4× · {result.created} criada(s) · {result.updated} atualizada(s)</div>
        </div>
      )}
      {!!errors.length && (
        <div className="mt-5 border border-destructive/50 bg-destructive/5 p-4" data-testid="admin-standalone-question-import-errors">
          <div className="font-mono-data font-bold text-destructive mb-2">Arquivo rejeitado — corrija as linhas abaixo</div>
          <div className="max-h-64 overflow-y-auto space-y-1 text-sm font-mono-data">
            {errors.slice(0, 100).map((error, index) => <div key={`${index}-${error}`}>{error}</div>)}
            {errors.length > 100 && <div>… e mais {errors.length - 100} erro(s)</div>}
          </div>
        </div>
      )}
    </Section>
  );
}

/* ---------- Admin-only credits ---------- */
function CreditsBlock() {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState("");
  const [amount, setAmount] = useState("100");
  const [description, setDescription] = useState("Bonus concedido pelo administrador");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const response = await api.get("/admin/credits/accounts");
      setData(response.data);
    } catch (error) { toast.error(fmtError(error)); }
  };
  useEffect(() => { load(); }, []);

  const choices = [
    ...((data?.targets?.schools || []).map((item) => ({
      value: `school|${item.id}`,
      label: `${item.kind === "personal" ? "Espaco pessoal" : "Instituicao"} · ${item.name}`,
    }))),
    ...((data?.targets?.teachers || []).filter((item) =>
      !(data?.targets?.schools || []).some((school) => school.owner_user_id === item.id)
    ).map((item) => ({ value: `user|${item.id}`, label: `Professor · ${item.name || item.email}` }))),
  ];

  const grant = async () => {
    const units = Number.parseInt(amount, 10);
    if (!target || !Number.isFinite(units) || units <= 0) {
      toast.error("Selecione o titular e informe uma quantidade positiva");
      return;
    }
    const [ownerType, ownerId] = target.split("|");
    setSaving(true);
    try {
      await api.post("/admin/credits/grant", {
        owner_type: ownerType,
        owner_id: ownerId,
        amount: units,
        description,
        idempotency_key: `admin-ui-${crypto.randomUUID()}`,
      });
      toast.success(`${units} creditos adicionados`);
      await load();
    } catch (error) { toast.error(fmtError(error)); }
    finally { setSaving(false); }
  };

  return (
    <Section eyebrow="cobranca · carteira auditavel" title="Creditos de correcao">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_160px_1fr_auto] gap-3 items-end">
        <div>
          <div className="label-eyebrow mb-1">professor ou instituicao</div>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger className="rounded-none" data-testid="credit-owner"><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>{choices.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><div className="label-eyebrow mb-1">quantidade</div><Input type="number" min="1" value={amount} onChange={(event) => setAmount(event.target.value)} className="rounded-none" /></div>
        <div><div className="label-eyebrow mb-1">motivo</div><Input value={description} onChange={(event) => setDescription(event.target.value)} className="rounded-none" /></div>
        <Button onClick={grant} disabled={saving || !target} className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="credit-grant">
          <Coins size={16} className="mr-2" /> {saving ? "Lancando..." : "Doar creditos"}
        </Button>
      </div>
      <div className="mt-5 flex items-center gap-3 border border-border p-3 text-sm">
        <span className={`h-2.5 w-2.5 rounded-full ${data?.enforced ? "bg-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]"}`} />
        <span><b>Cobranca {data?.enforced ? "ativa" : "em modo demonstracao"}.</b> Uma unidade corresponde a uma pagina/cartao enviado; paginas que falharem antes da correcao sao estornadas automaticamente.</span>
      </div>
      <div className="mt-4 border border-border divide-y divide-border max-h-72 overflow-y-auto">
        {(data?.accounts || []).map((account) => <div key={account.id} className="p-3 flex justify-between gap-4 text-sm">
          <div><b className="font-mono-data">{account.owner_name || account.owner_id}</b><span className="block text-xs text-muted-foreground">{account.owner_type === "school" ? "instituicao/espaco" : "professor"}</span></div>
          <div className="font-display text-2xl font-black text-primary">{account.unlimited ? "∞" : account.balance}</div>
        </div>)}
      </div>
    </Section>
  );
}

/* ---------- Admin-only transactional email diagnostics ---------- */
function EmailDeliveryBlock({ defaultEmail }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState(defaultEmail || "");
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [releasing, setReleasing] = useState("");

  const load = async () => {
    try {
      const response = await api.get("/admin/email/status");
      setData(response.data);
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!email.trim()) return;
    setTesting(true);
    try {
      await api.post("/admin/email/test", { email: email.trim() });
      toast.success("E-mail de teste aceito pelo provedor");
      await load();
    } catch (error) {
      toast.error(fmtError(error));
      await load();
    } finally {
      setTesting(false);
    }
  };

  const providerLabel = {
    amazon_ses_smtp: "Amazon SES · SMTP",
    smtp: "Servidor SMTP",
    resend: "Resend",
    disabled: "Desativado",
  }[data?.provider] || data?.provider || "—";
  const runtime = data?.runtime || {};
  const feedback = data?.feedback || {};
  const quota = data?.ses_quota || {};
  const fallback = data?.fallback || {};

  const releaseSuppression = async (destination) => {
    if (!window.confirm(`Liberar novos envios para ${destination}? Faça isso somente depois de confirmar que o endereço foi corrigido e deseja receber mensagens.`)) return;
    setReleasing(destination);
    try {
      await api.delete("/admin/email/suppressions", { params: { email: destination } });
      toast.success("Bloqueio de entrega removido");
      await load();
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setReleasing("");
    }
  };

  return (
    <Section eyebrow="infraestrutura · e-mail transacional" title="Entrega de e-mails">
      {loading ? <div className="label-eyebrow">carregando…</div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-8 gap-3">
            <div className="border border-border p-4">
              <div className="label-eyebrow">provedor ativo</div>
              <div className="font-mono-data font-bold mt-2">{providerLabel}</div>
            </div>
            <div className="border border-border p-4">
              <div className="label-eyebrow">configuração</div>
              <div className={`font-mono-data font-bold mt-2 ${data?.configured ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                {data?.configured ? "PRONTA" : "INCOMPLETA"}
              </div>
            </div>
            <div className="border border-border p-4">
              <div className="label-eyebrow">enviados neste processo</div>
              <div className="font-display text-2xl font-black mt-1">{runtime.sent || 0}</div>
            </div>
            <div className="border border-border p-4">
              <div className="label-eyebrow">falhas neste processo</div>
              <div className={`font-display text-2xl font-black mt-1 ${(runtime.failed || 0) > 0 ? "text-destructive" : ""}`}>{runtime.failed || 0}</div>
            </div>
            <div className="border border-border p-4">
              <div className="label-eyebrow">envios impedidos</div>
              <div className={`font-display text-2xl font-black mt-1 ${(runtime.suppressed || 0) > 0 ? "text-[hsl(var(--warning))]" : ""}`}>{runtime.suppressed || 0}</div>
            </div>
            <div className="border border-border p-4">
              <div className="label-eyebrow">destinatários bloqueados</div>
              <div className={`font-display text-2xl font-black mt-1 ${(feedback.active_suppressions || 0) > 0 ? "text-destructive" : ""}`}>{feedback.active_suppressions || 0}</div>
            </div>
            <div className="border border-border p-4" data-testid="ses-daily-quota">
              <div className="label-eyebrow">cota SES · 24h</div>
              <div className={`font-display text-2xl font-black mt-1 ${quota.reached ? "text-destructive" : ""}`}>
                {quota.used || 0}/{quota.limit || 200}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">restam {quota.remaining ?? 200}</div>
            </div>
            <div className="border border-border p-4" data-testid="email-fallback-status">
              <div className="label-eyebrow">contingência</div>
              <div className={`font-mono-data font-bold mt-2 ${fallback.available ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
                {fallback.available ? "RESEND PRONTO" : "INCOMPLETA"}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">usado {runtime.fallback_sent || 0} vez(es)</div>
            </div>
          </div>

          <div className="mt-4 border border-border p-4 text-sm grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-2">
            <div><span className="text-muted-foreground">Remetente SES:</span> <b>{data?.sender_name || "—"} &lt;{data?.smtp?.sender_email || "não configurado"}&gt;</b></div>
            <div><span className="text-muted-foreground">Remetente Resend:</span> <b>{data?.sender_name || "—"} &lt;{data?.resend?.sender_email || "não configurado"}&gt;</b></div>
            <div><span className="text-muted-foreground">Servidor:</span> <b>{data?.smtp?.host || "não se aplica"}{data?.smtp?.host ? `:${data.smtp.port}` : ""}</b></div>
            <div><span className="text-muted-foreground">Criptografia:</span> <b>{data?.smtp?.use_ssl ? "TLS implícito" : data?.smtp?.use_starttls ? "STARTTLS" : "não configurada"}</b></div>
            <div><span className="text-muted-foreground">Último sucesso:</span> <b>{runtime.last_success_at ? new Date(runtime.last_success_at).toLocaleString("pt-BR") : "nenhum neste processo"}</b></div>
            <div><span className="text-muted-foreground">Último provedor:</span> <b>{({ amazon_ses_smtp: "Amazon SES", resend: "Resend", smtp: "SMTP" })[runtime.last_provider] || "nenhum neste processo"}</b></div>
          </div>

          <div className="mt-4 border border-primary/40 bg-primary/5 p-4 text-sm" data-testid="email-failover-explanation">
            O Amazon SES é o provedor principal até {quota.limit || 200} reservas na janela de 24 horas. Ao atingir a cota ou receber uma falha confirmada do SES, o sistema tenta o Resend automaticamente. Falhas SMTP ambíguas não são reenviadas para evitar e-mail duplicado.
          </div>

          {runtime.last_error && (
            <div className="mt-4 border-2 border-destructive bg-destructive/5 p-4 text-sm text-destructive" data-testid="email-last-error">
              <b>Última falha:</b> {runtime.last_error}
            </div>
          )}

          <div className="mt-4 border border-border p-4 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="label-eyebrow">retorno automático Amazon SES</div>
                <div className={`font-mono-data font-bold mt-2 ${feedback.configured ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
                  {feedback.configured ? "SNS CONFIGURADO" : "AGUARDANDO TÓPICO SNS"}
                </div>
              </div>
              <div className="font-mono-data text-xs text-muted-foreground">
                hard bounces: <b>{feedback.hard_bounces || 0}</b> · reclamações: <b>{feedback.complaints || 0}</b>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Webhook: <code>{feedback.webhook_path || "/api/webhooks/aws/ses"}</code>. Somente mensagens com tópico autorizado e assinatura AWS válida são aceitas.
            </p>
          </div>

          {(feedback.recent_suppressions || []).length > 0 && (
            <div className="mt-4 border border-border divide-y divide-border" data-testid="email-suppression-list">
              <div className="p-3 label-eyebrow">endereços com envio suspenso</div>
              {feedback.recent_suppressions.map((item) => (
                <div key={item.id || item.email} className="p-3 flex flex-col md:flex-row md:items-center justify-between gap-3 text-sm">
                  <div>
                    <b className="font-mono-data">{item.email}</b>
                    <span className="block text-xs text-muted-foreground mt-1">
                      {item.reason === "complaint" ? "Reclamação do destinatário" : "Endereço rejeitado permanentemente"}
                      {item.detail ? ` · ${item.detail}` : ""}
                    </span>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-none font-mono-data uppercase tracking-widest text-[10px]" disabled={releasing === item.email} onClick={() => releaseSuppression(item.email)}>
                    {releasing === item.email ? "Liberando…" : "Liberar após correção"}
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <div className="label-eyebrow mb-1">destinatário do diagnóstico</div>
              <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} className="rounded-none" placeholder="administrador@saki-x.net" data-testid="email-test-destination" />
            </div>
            <Button onClick={sendTest} disabled={testing || !data?.configured || !email.trim()} className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="email-send-test">
              <PaperPlaneTilt size={16} className="mr-2" /> {testing ? "Enviando…" : "Enviar teste"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3 flex gap-2 items-start">
            <EnvelopeSimple size={16} className="shrink-0" /> Senhas SMTP e chaves permanecem exclusivamente no arquivo <code>.env</code> do servidor e nunca são enviadas ao navegador.
          </p>
        </>
      )}
    </Section>
  );
}

function ProfessorWorkspaceBlock() {
  const actions = [
    { to: "/exams", title: "Provas", text: "Criar, corrigir e apresentar provas ao vivo." },
    { to: "/questions", title: "Banco de questões", text: "Cadastrar, pesquisar e selecionar questões." },
    { to: "/students", title: "Alunos", text: "Consultar alunos e sua evolução acadêmica." },
    { to: "/performance", title: "Desempenho", text: "Comparar resultados e encontrar pontos de atenção." },
  ];
  return (
    <Section eyebrow="espaço de trabalho" title="Área do professor">
      <p className="text-sm text-muted-foreground mb-5 max-w-3xl">
        A administração mostra apenas os recursos compatíveis com o seu papel. Cadastros institucionais e configurações do servidor permanecem protegidos.
      </p>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {actions.map((item) => (
          <Link key={item.to} to={item.to} className="border border-border p-5 hover:border-primary hover:bg-primary/5 transition-colors">
            <div className="font-display text-xl font-bold">{item.title}</div>
            <div className="text-sm text-muted-foreground mt-2">{item.text}</div>
          </Link>
        ))}
      </div>
    </Section>
  );
}

/* ---------- Page shell ---------- */
export default function AdminPage() {
  const { user } = useAuth();
  const [capabilities, setCapabilities] = useState(null);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get("/administration/capabilities")
      .then((response) => setCapabilities(response.data))
      .catch((requestError) => setError(fmtError(requestError)));
  }, [user]);

  useEffect(() => {
    const available = capabilities?.access_sections || [];
    if (available.length && !available.includes(activeSection)) setActiveSection(available[0]);
  }, [capabilities, activeSection]);

  if (user === undefined || (user && capabilities === null && !error)) {
    return <div className="p-16 label-eyebrow">carregando…</div>;
  }
  if (!user) return null;
  if (error || !capabilities?.can_access) {
    return (
      <div className="max-w-3xl mx-auto px-8 py-16">
        <div className="border border-destructive bg-destructive/5 p-8">
          <div className="label-eyebrow text-destructive mb-2">acesso restrito</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Administração indisponível.</h1>
          <p className="text-muted-foreground mt-3">
            Este perfil não possui um espaço administrativo ou pedagógico atribuído.
          </p>
        </div>
      </div>
    );
  }

  const sectionInfo = {
    admin_general: { label: "Admin geral", description: "Servidor, segurança, créditos, integrações e manutenção." },
    school: { label: "Gestão da escola", description: "Semestres, turmas, alunos e estrutura institucional." },
    stage: { label: "Gestão de etapas", description: "Matriz, etapas, disciplinas, docentes e gestores." },
    professor: { label: "Professor", description: "Atalhos e ferramentas de trabalho docente." },
  };
  const availableSections = (capabilities?.access_sections || []).filter((key) => sectionInfo[key]);

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-10">
      <div className="mb-8 border-b border-border pb-6">
        <div className="label-eyebrow mb-2">administração hierárquica</div>
        <h1 className="font-display text-5xl font-black tracking-tighter leading-none">
          Estrutura e pessoas.
        </h1>
        <p className="text-muted-foreground mt-3 text-sm max-w-[720px]">
          Cada pessoa vê somente a sua área: administrador geral → gestor de escola → gestor de etapa → professor. O nome atual é gestor de etapa; gestor de turma permanece apenas como compatibilidade histórica.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-7" data-testid="administration-sections">
        {availableSections.map((key) => (
          <button key={key} type="button" onClick={() => setActiveSection(key)}
            className={`border p-4 text-left transition-colors ${activeSection === key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
            data-testid={`administration-section-${key}`}>
            <div className="font-display text-xl font-bold">{sectionInfo[key].label}</div>
            <div className={`text-xs mt-2 ${activeSection === key ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{sectionInfo[key].description}</div>
          </button>
        ))}
      </div>

      {activeSection === "admin_general" && user.is_admin && <>
        <ExamStyleManagementBlock />
        <LayoutAccessBlock />
        <SchoolLayoutAccessBlock admin />
        <MembershipsBlock capabilities={capabilities} allowedRoles={["gestor_escola"]} title="Gestores de escola" />
        <ImpersonationBlock />
        <MobileStudentTestBlock />
        <EmailDeliveryBlock defaultEmail={user.email} />
        <CreditsBlock />
        <CorrectionMaintenanceBlock />
        <MedcofImportBlock />
      </>}

      {activeSection === "school" && <>
        <ExamStyleManagementBlock />
        <SchoolLayoutAccessBlock />
        <SchoolsBlock />
        <SemestersBlock capabilities={capabilities} />
        <ClassesBlock capabilities={capabilities} />
        <StudentStampsBlock />
        <MembershipsBlock capabilities={capabilities} allowedRoles={["gestor_etapa"]} title="Gestores de etapa" />
      </>}

      {activeSection === "stage" && <>
        <ExamStyleManagementBlock />
        <MembershipsBlock capabilities={capabilities} allowedRoles={["professor"]} title="Adicionar professores" />
        <AcademicStructureBlock capabilities={capabilities} />
      </>}

      {activeSection === "professor" && <>
        <ExamStyleManagementBlock />
        <ProfessorWorkspaceBlock />
        {user.is_admin && <ProfessorImportBlock />}
        {user.is_admin && <ExamQuestionImportBlock />}
        {user.is_admin && <StandaloneQuestionImportBlock />}
      </>}
    </div>
  );
}
